import Editor, { EditorProps } from '@monaco-editor/react'
import React from 'react'

import { useDetectTheme } from '../hooks'

export type SupportedEditorLanguage = 'typescript' | 'json'

// NOTE: I am redefining this enum since `monaco-editor` is a dev dependency. The
// types are useful for development, but we only really need to export this one.
export enum CodeEditorValidationSeverity {
  Hint = 1,
  Info = 2,
  Warning = 4,
  Error = 8,
}

export type CodeEditorValidationResult = {
  severity: CodeEditorValidationSeverity
  message: string
  startLineNumber: number
  endLineNumber: number
}

export type CodeEditorProps = {
  /** The language used for syntax highlighting and snippets. Default is `json` */
  language?: SupportedEditorLanguage
  /** An optional callback to take in the editor validation results on change events. */
  onValidate?: (results: CodeEditorValidationResult[]) => void
} & Omit<EditorProps, 'language' | 'onValidate'>

type BaseCodeBlock = Record<SupportedEditorLanguage, string>
const baseCodeBlocks: BaseCodeBlock = {
  typescript: ['function foo() {', '\tconsole.log("Hello world!");', '}'].join(
    '\n',
  ),
  json: ['{', '    ', '}'].join('\n'),
}

// NOTE: unfortunately there are some issues mocking this editor for jest https://github.com/suren-atoyan/monaco-react/issues/88
// https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.IStandaloneEditorConstructionOptions.html
export default function CodeEditor({
  language = 'json',
  theme: themeOverride,
  onValidate,
  ...rest
}: CodeEditorProps) {
  const theme = useDetectTheme()

  const getSeverity = (level: number) => {
    if (level === 1) {
      return CodeEditorValidationSeverity.Hint
    } else if (level === 2) {
      return CodeEditorValidationSeverity.Info
    } else if (level === 4) {
      return CodeEditorValidationSeverity.Warning
    } else {
      return CodeEditorValidationSeverity.Error
    }
  }

  return (
    <Editor
      language={language}
      defaultValue={baseCodeBlocks[language]}
      theme={themeOverride || theme === 'light' ? 'vs' : 'vs-dark'}
      onValidate={(markers) => {
        if (onValidate) {
          const results: CodeEditorValidationResult[] = markers.map(
            ({
              severity,
              message,
              startColumn,
              startLineNumber,
              endLineNumber,
            }) => ({
              severity: getSeverity(severity),
              message,
              startColumn,
              startLineNumber,
              endLineNumber,
            }),
          )
          onValidate(results)
        }
      }}
      {...rest}
    />
  )
}
